import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import RouteInfo from 'react/components/RouteMonitor/RoutesList/list/RouteInfo';
import RouteSizes from 'react/components/QuickAssignment/RouteItem/RouteSizes';

import DriverAssignmentItem from './DriverAssignmentItem';
import EquipmentAssignmentItem from './EquipmentAssignmentItem';
import HelperAssignmentItem from './HelperAssignmentItem';
import QuickAssignmentAssignLoading from './AssignLoading';

import {
  getDriverAssignments,
  getEquipmentAssignments,
  getHelperAssignments,
} from 'react/api/selectors/quickAssignment';
import { actionCreators } from 'react/api/actions/quickAssignment';
import { getResourceTo } from 'react/utils/utils';

function ProgressBarAndRouteStatus({ route }) {
  const dispatch = useDispatch();

  const driverAssignments = useSelector((state) =>
    getDriverAssignments(state, route.id)
  );
  const equipmentAssignments = useSelector((state) =>
    getEquipmentAssignments(state, route.id)
  );
  const helperAssignments = useSelector((state) =>
    getHelperAssignments(state, route.id)
  );

  const assignsLoading = useSelector((state) =>
    state.quickAssignment.getIn(['assignmentsLoading', route.id], {})
  );

  const itemsCanBeRemoved = useMemo(
    () => route.status !== 'ARRIVED_ORIGIN' && route.status !== 'COMPLETED',
    [route]
  );

  function unassignDriverRequest(assignmentId, successCallback, errorCallback) {
    const payload = {
      assignmentId,
      routeId: route.id
    };

    const model = new App.Models.RouteDriverAssignment();
    model.unassignToRoute(
      payload,
      () => {
        successCallback();
        dispatch(actionCreators.unassignDriver(payload));
      },
      () => {
        errorCallback();
      }
    );
  }

  function unassignHelperRequest(assignmentId, successCallback, errorCallback) {
    const payload = {
      assignmentId,
      routeId: route.id
    };

    const model = new App.Models.RouteHelperAssignment();
    model.unassignToRoute(
      payload,
      () => {
        successCallback();
        dispatch(actionCreators.unassignHelper(payload));
      },
      () => {
        errorCallback();
      }
    );
  }

  function unassignDriver(assignment, successCallback, errorCallback) {
    const { id, driver } = assignment;

    if (driver.driverType === 'DEFAULT') {
      unassignDriverRequest(id, successCallback, errorCallback);
    } else {
      unassignHelperRequest(id, successCallback, errorCallback);
    }
  }

  function unassignHelper(assignment, successCallback, errorCallback) {
    const { id } = assignment;

    unassignHelperRequest(id, successCallback, errorCallback);
  }

  function updatePrimaryEquipment(newEquipmentToBePrimary) {
    if (newEquipmentToBePrimary.equipment.principal) return;

    const model = new App.Models.RouteEquipmentAssignment();

    const updatedEquipmentAssignments = equipmentAssignments.map(
      (assignment) => ({
        route: {
          id: route.id
        },
        equipment: {
          id: assignment.equipment.id
        },
        principal:
          assignment.equipment.id === newEquipmentToBePrimary.equipment.id
      })
    );

    const options = {
      routeEquipments: updatedEquipmentAssignments,
      routeId: route.id
    };

    const success = function () {
      const routeRouter = new App.Routers.Route();

      const successEquipmentsByRoute = (data) => {
        dispatch(
          actionCreators.updateEquipmentAssignments({
            assignments: data,
            routeId: route.id
          })
        );
      };

      routeRouter.getEquipmentsByRoute(options, successEquipmentsByRoute);
    };

    model.assignToRoute(options, success);
  }

  return (
    <div className="progressBarAndRouteStatus" data-routestatus={route.status}>
      <div data-testid="route-info-item" className="route-info-item">
        <div className="routeMonitorRouteItem">
          <RouteInfo route={{ route }} />
        </div>
        <div className="metadata">
          <p data-testid="route-origin">
            <span>{getResourceTo('route.originLocationID')}: </span>
            {route.origin?.description}
          </p>
          <p data-testid="route-destination">
            <span>{getResourceTo('route.destionationLocationID')}: </span>
            {route.destination?.description}
          </p>
          <p data-testid="route-organization">
            <span>{getResourceTo('route.organization')}: </span>
            {route.organization?.description}
          </p>
        </div>
      </div>
      <div className="assignments">
        <div className="drivers">
          <strong>{getResourceTo('SpeedAssignment.driversLabel')}</strong>
          {driverAssignments.map((assignment) => (
            <DriverAssignmentItem
              key={assignment.id}
              assignment={assignment}
              driverCanBeRemoved={itemsCanBeRemoved}
              unassignDriver={unassignDriver}
            />
          ))}
          <QuickAssignmentAssignLoading
            shouldShow={!!assignsLoading?.get?.('driver')}
            name={assignsLoading?.get?.('driver')}
          />
          <div className="driver-helper-drop-container" />
        </div>
        <div className="helpers">
          <strong>{getResourceTo('SpeedAssignment.helpersLabel')}</strong>
          {helperAssignments.map((assignment) => (
            <HelperAssignmentItem
              key={assignment.id}
              assignment={assignment}
              canBeRemoved={itemsCanBeRemoved}
              unassignHelper={unassignHelper}
            />
          ))}
          <QuickAssignmentAssignLoading
            shouldShow={!!assignsLoading?.get?.('helper')}
            name={assignsLoading?.get?.('helper')}
          />
          <div className="driver-helper-drop-container" />
        </div>
        <div className="equipments">
          <strong>{getResourceTo('SpeedAssignment.equipmentsLabel')}</strong>
          {equipmentAssignments.map((assignment) => (
            <EquipmentAssignmentItem
              key={assignment.id}
              assignment={assignment}
              canBeRemoved={itemsCanBeRemoved}
              routeId={route.id}
              updatePrimaryEquipment={updatePrimaryEquipment}
            />
          ))}
          <QuickAssignmentAssignLoading
            shouldShow={!!assignsLoading?.get?.('equipment')}
            name={assignsLoading?.get?.('equipment')}
          />
          <div
            className="equipment-drop-container"
          />
        </div>
      </div>
      <RouteSizes route={route} />
    </div>
  );
}

ProgressBarAndRouteStatus.propTypes = {
  route: PropTypes.object.isRequired
};

export {ProgressBarAndRouteStatus};
